import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import { Pin } from "@/components/icons"
import { formatFriendlyDateTime } from "@/lib/dates"
import { cn } from "@/lib/utils"

import { useAgentWorkingState } from "../hooks/use-agent-working-state"
import {
  activeWorkingStateFacts,
  formatWorkingStateValue,
  workingStateFactLabel,
} from "../utils/agent-working-state-utils"
import {
  AGENT_RAIL_HEADER_CLASS,
  AGENT_RAIL_LABEL_CLASS,
  AGENT_RAIL_META_CLASS,
  AGENT_RAIL_SECTION_CLASS,
} from "../utils/agents-utils"

type AgentWorkingStateSectionProps = {
  agentSlug: string
}

/**
 * "Working state" rail section on the agent landing page (#3023), read-only:
 * the structured facts the agent has established and a human has confirmed —
 * value, source and date. Transparency by design: users see exactly the
 * numbers the agent itself reads, independent of which thread established
 * them (B6: agent-scoped). Nothing renders while the layer is empty.
 */
export function AgentWorkingStateSection({ agentSlug }: AgentWorkingStateSectionProps) {
  const { t, i18n } = useTranslation()
  const workingStateQuery = useAgentWorkingState(agentSlug)

  const facts = useMemo(
    () => activeWorkingStateFacts(workingStateQuery.data ?? []),
    [workingStateQuery.data]
  )

  // An empty (or unavailable) layer renders nothing: most agents never use
  // working state, and an always-on empty section would just push the thread
  // list down.
  if (!workingStateQuery.isSuccess || facts.length === 0) return null

  return (
    <section
      aria-label={t("agents.workingState.title")}
      className={AGENT_RAIL_SECTION_CLASS}
      data-testid="agent-working-state-section"
    >
      <div className={AGENT_RAIL_HEADER_CLASS}>
        <span className={AGENT_RAIL_LABEL_CLASS}>{t("agents.workingState.title")}</span>
        <span className={AGENT_RAIL_META_CLASS}>{facts.length}</span>
      </div>
      <div className="flex flex-col gap-1.5">
        {facts.map((fact) => (
          <div
            key={fact.id}
            className="bg-card flex flex-col gap-0.5 rounded-md border px-3 py-2.5"
            data-testid="agent-working-state-fact"
          >
            <span className={cn(AGENT_RAIL_META_CLASS, "line-clamp-1")}>
              {workingStateFactLabel(fact)}
            </span>
            <span className="flex items-center gap-1.5 text-[13px] leading-snug font-semibold">
              {fact.active ? formatWorkingStateValue(fact.active, i18n.language) : null}
              {fact.pinned ? (
                <Pin
                  aria-label={t("agents.workingState.pinned")}
                  className="text-muted-foreground size-3"
                />
              ) : null}
            </span>
            {fact.active ? (
              <span className={cn(AGENT_RAIL_META_CLASS, "line-clamp-1")}>
                {t("agents.workingState.source", { source: fact.active.source })}
                {" · "}
                {formatFriendlyDateTime(fact.active.provenance.recorded_at)}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  )
}
